import * as React from 'react';

import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../lib/utils';
import { Popover, PopoverAnchor, PopoverContent } from './popover';

export type ComboboxOption = {
    value: string;
    label: string;
    // Extra search terms beyond the label — e.g. an abbreviation the label itself doesn't
    // contain as a substring ("NY" for "New York").
    keywords?: string[];
};

type Props = {
    options: ComboboxOption[];
    value?: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    emptyMessage?: string;
    id?: string;
    'aria-invalid'?: boolean;
    disabled?: boolean;
    className?: string;
};

const fieldClassName =
    'flex h-10 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-input-background px-3 text-base text-foreground outline-none transition-[color,box-shadow] cursor-text';

const optionClassName =
    'relative flex min-h-10 w-full cursor-default select-none items-center rounded-xs py-1.5 pl-2 pr-8 text-base leading-none text-popover-foreground outline-none data-[highlighted]:bg-accent/50';

const optionIndicatorClassName = 'absolute right-2 flex size-4 items-center justify-center';

const scrollIndicatorClassName = 'flex shrink-0 justify-center py-1 text-popover-foreground';

type ScrollState = { canScrollUp: boolean; canScrollDown: boolean };

function readScrollState(list: HTMLElement): ScrollState {
    return {
        canScrollUp: list.scrollTop > 1,
        canScrollDown: list.scrollHeight - list.clientHeight - list.scrollTop > 1,
    };
}

// `offsetTop` rather than bounding rects: the popover animates in under a scale transform, which
// would skew rect-based maths on the very frame the list opens.
function scrollOptionIntoView(list: HTMLElement, option: HTMLElement, align: 'center' | 'nearest') {
    if (align === 'center') {
        list.scrollTop = option.offsetTop - (list.clientHeight - option.offsetHeight) / 2;
        return;
    }
    // Stop at the list's own padding so the first and last option land flush with the ends.
    const { paddingTop, paddingBottom } = getComputedStyle(list);
    const top = option.offsetTop - Number.parseFloat(paddingTop);
    const bottom = option.offsetTop + option.offsetHeight + Number.parseFloat(paddingBottom);
    if (top < list.scrollTop) {
        list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
    }
}

function matchesQuery(option: ComboboxOption, query: string): boolean {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return true;
    const haystack = [option.label, ...(option.keywords ?? [])].join(' ').toLowerCase();
    return haystack.includes(trimmed);
}

// A type-to-filter autocomplete: a plain `<input>` we fully own, not cmdk's `Command.Input` —
// that component claims `id`/`aria-expanded`/`onChange` unconditionally, breaking real typing.
export function Combobox({
    options,
    value,
    onValueChange,
    placeholder = 'Select…',
    emptyMessage = 'No results found',
    id,
    'aria-invalid': ariaInvalid,
    disabled,
    className,
}: Props) {
    const [open, setOpen] = React.useState(false);
    // What's in the field while open — typed text, or the selected label right after opening.
    // Ignored while closed: `displayValue` below reads the selected option directly instead.
    const [text, setText] = React.useState('');
    // False until the user types, so opening a field that already has a value lists every option
    // rather than only the one matching the selected label.
    const [isFiltering, setIsFiltering] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(0);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const listboxId = React.useId();
    const optionIdPrefix = React.useId();
    // Set right before a commit's `closeList()`, consumed by the effect below — narrower than
    // "select whenever the list closes," which would also hijack focus after Escape/blur.
    const justCommittedRef = React.useRef(false);
    // Set on open, consumed by the first scroll effect that finds the list: the selected option
    // is centred once, and later keyboard moves only scroll as far as needed.
    const centerOnOpenRef = React.useRef(false);
    // State, not a ref: the list mounts a render after `open` flips (the popover portals), and the
    // effects below have to re-run once it exists.
    const [listElement, setListElement] = React.useState<HTMLDivElement | null>(null);
    const [scrollState, setScrollState] = React.useState<ScrollState>({
        canScrollUp: false,
        canScrollDown: false,
    });

    const selected = options.find((option) => option.value === value);
    const selectedLabel = selected?.label ?? '';
    const displayValue = open ? text : selectedLabel;
    const filtered = options.filter((option) =>
        matchesQuery(option, open && isFiltering ? text : ''),
    );
    const highlighted = filtered[highlightedIndex];

    React.useEffect(() => {
        if (!listElement || !highlighted) return;
        const option = document.getElementById(`${optionIdPrefix}-${options.indexOf(highlighted)}`);
        if (!option) return;
        scrollOptionIntoView(listElement, option, centerOnOpenRef.current ? 'center' : 'nearest');
        centerOnOpenRef.current = false;
        setScrollState(readScrollState(listElement));
    }, [listElement, highlighted, optionIdPrefix, options]);

    // Typing changes how much there is to scroll without any scroll event.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `filtered.length` is the trigger
    React.useEffect(() => {
        if (listElement) setScrollState(readScrollState(listElement));
    }, [listElement, filtered.length]);

    // Reselects the field's text once a commit's `closeList()` has rendered the picked label —
    // otherwise the next keystroke appends to the label instead of replacing it.
    React.useEffect(() => {
        if (open || !justCommittedRef.current) return;
        justCommittedRef.current = false;
        inputRef.current?.select();
    }, [open]);

    function openList() {
        if (open) return;
        setText(selectedLabel);
        setIsFiltering(false);
        setHighlightedIndex(
            Math.max(
                0,
                options.findIndex((option) => option.value === value),
            ),
        );
        centerOnOpenRef.current = true;
        setOpen(true);
    }

    function closeList() {
        setOpen(false);
    }

    function commitSelection(option: ComboboxOption) {
        onValueChange(option.value);
        justCommittedRef.current = true;
        closeList();
    }

    function handleFocus() {
        openList();
        inputRef.current?.select();
    }

    function handleClick() {
        if (open) return;
        openList();
        inputRef.current?.select();
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (!open) setOpen(true);
        setText(event.target.value);
        setIsFiltering(true);
        setHighlightedIndex(0);
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                if (!open) {
                    openList();
                    return;
                }
                setHighlightedIndex((current) => Math.min(current + 1, filtered.length - 1));
                return;
            }
            case 'ArrowUp': {
                if (!open) return;
                event.preventDefault();
                setHighlightedIndex((current) => Math.max(current - 1, 0));
                return;
            }
            case 'Home': {
                if (!open) return;
                event.preventDefault();
                setHighlightedIndex(0);
                return;
            }
            case 'End': {
                if (!open) return;
                event.preventDefault();
                setHighlightedIndex(filtered.length - 1);
                return;
            }
            case 'Enter': {
                // Always prevented, open or closed — this input sits inside a `<form>`, and an
                // unhandled Enter would otherwise submit it.
                event.preventDefault();
                if (open && highlighted) commitSelection(highlighted);
                return;
            }
            case 'Escape': {
                if (!open) return;
                event.preventDefault();
                closeList();
                return;
            }
            default:
                return;
        }
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <div
                    ref={containerRef}
                    className={cn(
                        fieldClassName,
                        'has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
                        // `input.tsx`'s focus/invalid reactions, re-keyed onto the child input via `has-[...]`
                        // (`has-[:focus-visible]`, not `has-[input:focus-visible]` — Tailwind's JIT scanner
                        // silently emits no rule for that form).
                        'has-[:focus-visible]:border-ring has-[:focus-visible]:shadow-[0_0_0_2px_var(--color-accent)]',
                        'has-[[aria-invalid=true]]:border-destructive',
                        'has-[[aria-invalid=true]]:has-[:focus-visible]:border-destructive has-[[aria-invalid=true]]:has-[:focus-visible]:shadow-[0_0_0_2px_color-mix(in_oklab,var(--color-destructive)_20%,transparent)] dark:has-[[aria-invalid=true]]:has-[:focus-visible]:shadow-[0_0_0_2px_color-mix(in_oklab,var(--color-destructive)_40%,transparent)]',
                        className,
                    )}
                >
                    <input
                        ref={inputRef}
                        id={id}
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-activedescendant={
                            open && highlighted
                                ? `${optionIdPrefix}-${options.indexOf(highlighted)}`
                                : undefined
                        }
                        aria-invalid={ariaInvalid}
                        disabled={disabled}
                        placeholder={placeholder}
                        value={displayValue}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onClick={handleClick}
                        onBlur={closeList}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
                    />
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label={open ? 'Close list' : 'Open list'}
                        disabled={disabled}
                        // Prevents the mousedown from stealing focus off the input — a click here
                        // should toggle the list without ever blurring the field.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                            if (open) {
                                closeList();
                            } else {
                                openList();
                            }
                            inputRef.current?.focus();
                            inputRef.current?.select();
                        }}
                    >
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                </div>
            </PopoverAnchor>
            <PopoverContent
                className="flex max-h-[300px] w-auto min-w-(--radix-popover-trigger-width) flex-col rounded-sm p-1"
                align="start"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                // A click inside the anchor is technically "outside" the content from Radix's
                // point of view — without this it would immediately re-close a list just opened.
                onInteractOutside={(event) => {
                    const target = event.target as Node | null;
                    if (containerRef.current && target && containerRef.current.contains(target)) {
                        event.preventDefault();
                    }
                }}
            >
                {scrollState.canScrollUp && (
                    <div
                        aria-hidden="true"
                        data-slot="combobox-scroll-up"
                        className={scrollIndicatorClassName}
                    >
                        <ChevronUp className="size-[15px]" />
                    </div>
                )}
                <div
                    ref={setListElement}
                    role="listbox"
                    id={listboxId}
                    aria-label={placeholder}
                    onScroll={(event) => setScrollState(readScrollState(event.currentTarget))}
                    onWheel={(event) => event.stopPropagation()}
                    onTouchMove={(event) => event.stopPropagation()}
                    className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-1 [scrollbar-width:thin]"
                >
                    {filtered.length === 0 ? (
                        <div className="py-6 text-center text-sm">{emptyMessage}</div>
                    ) : (
                        filtered.map((option, index) => {
                            const isHighlighted = index === highlightedIndex;
                            return (
                                <div
                                    key={option.value}
                                    id={`${optionIdPrefix}-${options.indexOf(option)}`}
                                    role="option"
                                    aria-selected={isHighlighted}
                                    className={optionClassName}
                                    data-highlighted={isHighlighted ? '' : undefined}
                                    // Keeps focus in the input through the click — the mousedown's
                                    // default focus-search can still hunt for a focusable ancestor.
                                    onMouseDown={(event) => event.preventDefault()}
                                    // Move, not enter: scrolling the list under a resting pointer fires
                                    // enter and would steal the highlight from the selected option.
                                    onMouseMove={() => setHighlightedIndex(index)}
                                    onClick={() => commitSelection(option)}
                                >
                                    {option.label}
                                    {option.value === value && (
                                        <span className={optionIndicatorClassName}>
                                            <Check className="h-4 w-4" />
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
                {scrollState.canScrollDown && (
                    <div
                        aria-hidden="true"
                        data-slot="combobox-scroll-down"
                        className={scrollIndicatorClassName}
                    >
                        <ChevronDown className="size-[15px]" />
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
